import React, { Component } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import ReactMarkdown from "react-markdown";
import classNames from "classnames";
import ProgressButton from "react-progress-button";
import { InvoiceService } from "@services";
import { INVOICE_STATUSES } from "@constants";
import utils from "@utils";
import Actions from "@redux/payments";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import {
	DisputeInvoiceModal,
	SendToContactModal,
	ConfirmationModal,
	EditInvoiceModal,
	Loader,
	Image,
	Tooltip,
} from "../../../components";

import "./PaymentDetails.scss";

export class PaymentDetails extends Component {
	constructor(props) {
		super(props);

		this.state = {
			loading: true,
			panelTabsExpanded: {
				notes: false,
				attachments: false,
				statusHistory: false,
			},
			invoice: {},
			type: "",
			status: {},
			progressButtons: {
				remindOfficer: "",
				resendInvoice: "",
			},
			originalAgencyNotes: "",
			showSendInvoiceModal: false,
		};

		const {
			flags: { removeInsurance },
		} = props;

		this.hasRemoveInsurance = !!removeInsurance;
		this._disputeInvoiceModal = React.createRef();
		this._editInvoiceModal = React.createRef();
		this._confirmationModal = React.createRef();
	}

	componentDidMount = () => {
		this.getInvoice();
		document.addEventListener("click", this.clickEventListener);
	};

	componentDidUpdate(prevProps) {
		const {
			router: {
				params: { invoiceId: prevId },
			},
		} = prevProps;
		const {
			router: {
				params: { invoiceId },
			},
		} = this.props;
		// If the component has already mounted and the invoiceId parameter changes without reloading, get new invoice details
		// Included here since notifications can route to this page
		if (prevId && invoiceId && prevId !== invoiceId) {
			this.getInvoice();
		}
	}

	componentWillUnmount() {
		document.removeEventListener("click", this.clickEventListener);
	}

	toggleSendInvoiceModal = () =>
		this.setState(prevState => ({ showSendInvoiceModal: !prevState.showSendInvoiceModal }));

	/** Retrieves the details for a specific invoice */
	getInvoice = () => {
		const { lang } = global;
		const {
			router: {
				push,
				params: { invoiceId },
			},
		} = this.props;
		const { originalAgencyNotes } = this.state;

		this.setState({ loading: true });

		InvoiceService.getInvoiceById(invoiceId)
			.then(({ data: invoice }) => {
				this.setState({
					loading: false,
					invoice: {
						...invoice,
						AgencyNotes: invoice.AgencyNotes || originalAgencyNotes || "",
					},
					type: this.getInvoiceType(invoice),
					originalAgencyNotes: invoice.AgencyNotes || originalAgencyNotes || "",
					status: this.getInvoiceStatusFromStatusId(invoice.StatusId, invoice.Status),
				});
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading invoice details"),
				});
				push("/payments/list");
			});
	};

	/**
	 * Displays the status of the invoice
	 * @param {Object} invoice The invoince object
	 * @returns {String} Determins the status of the invoice
	 */
	getInvoiceType = invoice => {
		const { user } = this.props;

		let type = "";
		if (
			invoice.StatusId === INVOICE_STATUSES.CREATED ||
			(invoice.StatusId === INVOICE_STATUSES.PENDING_REVIEW && user.isAgencyAdmin())
		) {
			type = "submission";
		} else if (
			(invoice.StatusId === INVOICE_STATUSES.PENDING_REVIEW &&
				(user.isLEA() || user.isCoordinator() || user.isSuperAdmin())) ||
			(invoice.StatusId === INVOICE_STATUSES.PENDING_CLIENT_REVIEW &&
				!user.isLEA() &&
				!user.isCoordinator()) ||
			(invoice.StatusId === INVOICE_STATUSES.AMENDED &&
				!user.isLEA() &&
				!user.isCoordinator())
		) {
			type = "review";
		} else if (
			invoice.StatusId === INVOICE_STATUSES.PENDING_PAYMENT ||
			invoice.StatusId === INVOICE_STATUSES.PAYMENT_REJECTED ||
			(invoice.StatusId === INVOICE_STATUSES.PENDING_CLIENT_REVIEW &&
				(user.isLEA() || user.isCoordinator())) ||
			(invoice.StatusId === INVOICE_STATUSES.AMENDED &&
				(user.isLEA() || user.isCoordinator()))
		) {
			type = "payment";
		} else if (invoice.StatusId === INVOICE_STATUSES.DISPUTED) {
			type = "dispute";
		} else if (
			invoice.StatusId === INVOICE_STATUSES.PENDING_PROCESSING ||
			invoice.StatusId === INVOICE_STATUSES.PAID ||
			invoice.StatusId === INVOICE_STATUSES.DEPOSITED ||
			invoice.StatusId === INVOICE_STATUSES.DEPOSIT_FAILED
		) {
			type = "paid";
		}
		return type;
	};

	/**
	 * Handles any actions that need to be performed on the invoice
	 * @param {String} action The action that will be invoked
	 */
	handleInvoiceAction = action => {
		const {
			user: {
				Organization: { Id: orgId },
			},
			updateStoreProperties,
			router: { push },
		} = this.props;
		const { invoice } = this.state;

		switch (action) {
			case "toReview":
				this.updateInvoiceStatus(INVOICE_STATUSES.AMENDED, "review");
				break;
			case "toPayment":
				this.updateInvoiceStatus(INVOICE_STATUSES.PENDING_PAYMENT, "review");
				break;
			case "dispute":
				this._disputeInvoiceModal.current.open(invoice.Id);
				break;
			case "edit":
				this._editInvoiceModal.current.open();
				break;
			case "send":
				if (invoice.AgencyId === orgId) this.toggleSendInvoiceModal();
				else this.sendInvoice(invoice.Id);
				break;
			case "paid":
				push({ pathname: `/payments/receipt/${invoice.TransactionId}` });
				break;
			case "payInvoice":
				updateStoreProperties({ activeInvoiceIds: [invoice.Id] });
				push({ pathname: "/payments/invoice", state: { InvoiceIds: [invoice.Id] } });
				break;
			case "recalculate":
				this.recalculateInvoice();
				break;
			default:
				break;
		}
	};

	/**
	 * Sends the invoice to the specific contacts email address
	 * @param {Number} invoiceId The unique id of the inovice
	 * @param {Number} contactId The unique id of the contact
	 * @param {Array} additionalContactEmails A list of additional emails taht will be BCC's when sending the invoice
	 */
	sendInvoice = (invoiceId, contactId, additionalContactEmails) => {
		const { lang } = global;
		const {
			router: { push },
		} = this.props;
		this.setState({ loading: true });

		const queryParams = {};
		if (contactId) queryParams.contactId = contactId;
		const data = { invoiceIds: [invoiceId], additionalContactEmails };
		InvoiceService.sendInvoiceToClient(queryParams, data)
			.then(() => {
				this.setState({ loading: false, showSendInvoiceModal: false });
				utils.notify({
					type: "success",
					message: lang.t("Invoice Sent"),
				});
				push({ pathname: "/payments/list/review" });
			})
			.catch(
				({
					response: {
						data: { Message },
					},
				}) => {
					this.setState({ loading: false, showSendInvoiceModal: false });
					utils.notify({
						type: "error",
						message: Message || lang.t("Error sending invoice(s)"),
					});
				},
			);
	};

	/**
	 * Updates the status of the invoice
	 * @param {String} newStatus The new status of the invoice
	 * @param {String} redirectTo Determines if the user will be redirected after updating the invoice status
	 */
	updateInvoiceStatus = (newStatus, redirectTo = "") => {
		const { lang } = global;
		const { router } = this.props;
		const {
			invoice: { Id },
		} = this.state;
		this.setState({ loading: true });

		InvoiceService.changeInvoiceUserStatus({ idArray: [Id], status: newStatus })
			.then(response => {
				this.setState({ loading: false });
				const {
					data: { Message },
				} = response;

				utils.notify({
					type: "success",
					message: Message,
					title: "Payments",
				});

				if (redirectTo) router.push({ pathname: `/payments/list/${redirectTo}` });
			})
			.catch(() => {
				this.setState({ loading: false });
				utils.notify({
					type: "error",
					message: lang.t("Error updating invoice"),
				});
			});
	};

	toggleCollapsingPanel = name => {
		this.setState(prevState => ({
			panelTabsExpanded: {
				...prevState.panelTabsExpanded,
				[name]: !prevState.panelTabsExpanded[name],
			},
		}));
	};

	/** Calls an API endpint which will notify the officer to submit their invoice  */
	remindOfficerSubmit = () => {
		const { lang } = global;
		const {
			invoice: { Id },
		} = this.state;

		InvoiceService.remindOfficerSubmit(Id)
			.then(({ data }) => {
				utils.notify({
					type: "success",
					message: data.Message,
				});

				this.setState(prevState => ({
					progressButtons: {
						...prevState.progressButtons,
						remindOfficer: "disabled",
					},
				}));
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error sending notification to the officer"),
				});
				this.setState(prevState => ({
					progressButtons: {
						...prevState.progressButtons,
						remindOfficer: "error",
					},
				}));
			});
	};

	/** Allows for resending the invoice if needed */
	resendInvoice = () => {
		const {
			invoice: { Id },
		} = this.state;
		InvoiceService.resendInvoiceToClient(Id)
			.then(({ data }) => {
				utils.notify({
					type: "success",
					message: data.Message,
				});

				this.setState(prevState => ({
					progressButtons: {
						...prevState.progressButtons,
						resendInvoice: "disabled",
					},
				}));
			})
			.catch(
				({
					response: {
						data: { Message: message },
					},
				}) => {
					utils.notify({
						type: "error",
						message,
					});
					this.setState(prevState => ({
						progressButtons: {
							...prevState.progressButtons,
							resendInvoice: "error",
						},
					}));
				},
			);
	};

	/** Recalculates the details of the invoice in case any updates have been made outside of the present page */
	recalculateInvoice = () => {
		const { lang } = global;
		const {
			invoice: { Id },
		} = this.state;

		this.setState({ loading: true });

		InvoiceService.recalculateInvoiceById(Id)
			.then(() => {
				this.getInvoice();
			})
			.catch(() => {
				this.setState({ loading: false });
				utils.notify({
					type: "error",
					message: lang.t("The invoice could not be recalculated"),
				});
			});
	};

	/** Displays a modal prompting the user to confirm the voiding of the invoice */
	confirmVoid = () => {
		const { lang } = global;
		this._confirmationModal.current.openModal({
			title: lang.t("Confirm"),
			message: lang.t("Are you sure you want to void this invoice_"),
			onConfirm: () => this.voidInvoice(),
		});
	};

	/** Voids the invoice if it has not already been voided in the past */
	voidInvoice = () => {
		const { lang } = global;
		const {
			invoice: { Id, StatusId, Status },
		} = this.state;

		// if the invoice has been previously voided notify the user
		if (StatusId === 11 && Status === "Void") {
			utils.notify({
				type: "error",
				message: lang.t("The invoice has already been voided"),
				timeOut: 1500,
			});
			return;
		}

		// api accepts an array of invoice ids
		InvoiceService.voidInvoice([Id])
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Invoice successfully voided"),
					timeOut: 1500,
				});
				this.getInvoice();
			})
			.catch(({ response: { data, status } }) => {
				// if a 409 is the response this means the invoice has been voided or has been proccessed through
				if (status === 409) {
					utils.notify({
						type: "error",
						message: data.Message || lang.t("The invoice has already been voided"),
						timeOut: 1500,
					});
				} else {
					utils.notify({
						type: "error",
						message: lang.t("The invoice could not be voided"),
						timeOut: 1500,
					});
				}
			});
	};

	downloadAllAttachments = () => {
		const { lang } = global;
		const {
			invoice: { Attachments },
		} = this.state;
		this.setState({ loading: true });

		const data = Attachments.map(({ FileName: fileName, AttachmentUrl: url }) => ({
			fileName,
			url,
		}));

		utils
			.downloadFiles(data, "attachments.zip")
			.then(() => this.setState({ loading: false }))
			.catch(() => {
				utils.notify({
					message: lang.t("Unable to load file"),
					title: lang.t("Error downloading attachments"),
					type: "error",
				});

				this.setState({ loading: false });
			});
	};

	updateAgencyNotes = event => {
		const { lang } = global;
		const {
			invoice: { Id, AgencyNotes },
		} = this.state;
		// stringifying the agency notes in order to pass in the agency notes param as standalone string
		InvoiceService.updateAgencyNotes(Id, JSON.stringify(AgencyNotes))
			.then(() => {
				// Notify only if the Save button is clicked
				if (event) {
					utils.notify({
						type: "success",
						message: `${lang.t("Disclaimer")} ${lang.t("Updated")}`,
					});
				}
				this.setState({
					originalAgencyNotes: AgencyNotes,
				});
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error saving notes"),
				});
			});
	};

	agencyNotesChange = e => {
		const {
			target: { value },
		} = e;
		this.setState(prevState => ({
			invoice: {
				...prevState.invoice,
				AgencyNotes: value,
			},
		}));
	};

	/** If the user has changed their notes update/save the Agency Notes before printing */
	handleOnPrint = () => {
		const { invoice: AgencyNotes, originalAgencyNotes } = this.state;
		if (AgencyNotes !== originalAgencyNotes) this.updateAgencyNotes();
		return window.print();
	};

	/** Download Invoice PDF to users device */
	downloadInvoicePDF = () => {
		const { lang } = global;
		const {
			invoice: { Id },
		} = this.state;
		this.setState({ loading: true });
		InvoiceService.downloadInvoicePDF(Id)
			.then(({ data }) => {
				utils.downloadFromBlobResponse(data, `invoice_${Id}.pdf`);
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("The invoice download failed"),
					timeOut: 1500,
				});
			})
			.finally(() => this.setState({ loading: false }));
	};

	/**
	 * Determines which status and styles will be applied when displaying the invoices status
	 * @param {Number} statusId The invoice status id
	 * @param {String} status The invoice status
	 * @returns {Object} An object containing both the status and its type
	 */
	getInvoiceStatusFromStatusId = (statusId, status) => {
		const result = {
			label: status,
			type: "success",
		};

		switch (statusId) {
			case INVOICE_STATUSES.PENDING_REVIEW:
			case INVOICE_STATUSES.AMENDED:
			case INVOICE_STATUSES.PENDING_PAYMENT:
			case INVOICE_STATUSES.PENDING_PROCESSING:
			case INVOICE_STATUSES.PENDING_CLIENT_REVIEW:
				result.type = "warning";
				break;
			case INVOICE_STATUSES.PAYMENT_REJECTED:
			case INVOICE_STATUSES.DISPUTED:
			case INVOICE_STATUSES.VOID:
				result.type = "danger";
				break;
			default:
				result.type = "success";
				break;
		}

		return result;
	};

	clickEventListener = ({ target }) => {
		const dropdown = document.getElementById("refund-menu");
		if (dropdown && dropdown.classList.contains("open") && !target.closest("#refund-menu")) {
			dropdown.classList.remove("open");
		}
	};

	toggleActionDropdown = () => {
		const actions = document.getElementById("refund-menu");
		if (actions.classList.contains("open")) actions.classList.remove("open");
		else actions.classList.add("open");
	};

	/** Displays a modal prompting the user to confirm the refund of the invoice */
	refundInsurance = event => {
		if (event) event.preventDefault();

		const { lang } = global;
		const { invoice } = this.state;
		this._confirmationModal.current.openModal({
			title: lang.t("Issue refund_"),
			message: `${lang.t("You are about to issue an RKInsurance refund in the amount of_")} $${invoice.RKInsurance}
						${lang.t("_to officer_")} ${invoice.Officer.Name} ${lang.t("_for invoice #_")}${invoice.Id}
						${lang.t("This action cannot be undone_")}`,
			confirmButton: `${lang.t("Yes, issue refund")}`,
			cancelButton: `${lang.t("No, Cancel")}`,
			onConfirm: () => {
				InvoiceService.refundRKInsurance(invoice.Id)
					.then(() => {
						utils.notify({
							type: "success",
							message: lang.t("RKInsurance transfer successfully initiated"),
							timeOut: 2000,
						});
					})
					.catch(e => {
						utils.notify({
							type: "error",
							message: e.message,
							timeOut: 1500,
						});
					});
			},
		});
	};

	render() {
		const { lang } = global;
		const {
			router,
			router: { push },
			user: { TimeZoneId, Organization },
			user,
		} = this.props;
		const {
			loading,
			invoice,
			status,
			panelTabsExpanded,
			type,
			progressButtons,
			showSendInvoiceModal,
			originalAgencyNotes,
		} = this.state;

		return (
			<div id="payment-details">
				<div className="container-fluid hidden-print">
					<div className="row">
						<div
							id="sub-nav"
							className="col-xs-12"
						>
							<button
								type="button"
								className="btn btn-link"
								onClick={() => router.goBack()}
								id="go-back"
							>
								<i className="rkf rkf-chevron-left" />
								{lang.t("Go")} {lang.t("Back")}
							</button>
						</div>
					</div>
				</div>
				<div className="container">
					<div className="row">
						<div className="col-sm-8 invoice-overview">
							<div id="invoice-header">
								<div className="row">
									<div className="col-xs-6">
										<span className={`hidden-print label label-${status.type}`}>
											{status.label}
										</span>
										<If condition={invoice.InvoiceEstimatePrePayment}>
											<br />
											<br />
											<span
												className="label label-success"
												title={lang.t(
													"This job has been prepaid, no overtime hours are allowed_",
												)}
											>
												{lang.t("Prepaid")}
											</span>
										</If>
									</div>
									<div className="col-xs-6 text-right">
										<Link
											to={`/jobs/${invoice.JobEventId}`}
											className="hidden-print"
										>
											{`${lang.t("Job")} #${invoice.JobEventId}`}
										</Link>
										<If condition={invoice.AssignmentId}>
											<br />
											{`${lang.t("Assignment")} #${invoice.AssignmentId}`}
										</If>
										<br />
										{`${lang.t("Invoice")} #${invoice.Id}`}
									</div>
								</div>
							</div>
							<div>
								<If condition={user.Organization.OrganizationLogo}>
									<img
										src={user.Organization.OrganizationLogo}
										className="org-logo visible-print-inline"
										alt="Agency Logo"
									/>
								</If>
								<h3>{lang.t("Job Details")}</h3>
								<ReactMarkdown>{invoice.JobDetails}</ReactMarkdown>
								<div className="row">
									<If condition={invoice.JobRate}>
										<div className="col-xs-4 col-sm-2 text-muted">
											{lang.t("Rate")}
										</div>
										<div className="col-xs-8 col-sm-4">{`$${invoice.JobRate}/hour`}</div>
									</If>
									<If condition={invoice.ClientName}>
										<div className="col-xs-4 col-sm-2 text-muted">
											{lang.t("Client")}
										</div>
										<div className="col-xs-8 col-sm-4">
											{invoice.ClientName}
										</div>
									</If>
									<If condition={invoice.JobLocation}>
										<div className="col-xs-4 col-sm-2 text-muted">
											{lang.t("Location")}
										</div>
										<div className="col-xs-8 col-sm-4">
											{invoice.JobLocation}
										</div>
									</If>
									<If condition={invoice.JobStartDateTime}>
										<div className="col-xs-4 col-sm-2 text-muted">
											{lang.t("Date")}
										</div>
										<div className="col-xs-8 col-sm-4">
											{utils.formatDate(
												`${invoice.JobStartDateTime}Z`,
												"en-US",
												TimeZoneId,
												"w-long",
											)}
										</div>
									</If>
								</div>
								<div className="row">
									<If condition={invoice.Officer}>
										<div className="col-xs-4 col-sm-2 text-muted">
											{lang.t("Officer")}
										</div>
										<div className="col-xs-8 col-sm-4">
											<Image
												className="img-circle pull-left"
												src={invoice.Officer.ImageURL}
												alt={invoice.Officer.Name}
											/>
											<div className="pull-left">
												{invoice.Officer.Name}
												<br />
												<If condition={invoice.Officer.MobilePhone}>
													{utils.formatPhoneNumber(
														invoice.Officer.MobilePhone,
													)}
													<br />
												</If>
												{utils.constructShortAddress(
													invoice.Officer.City,
													invoice.Officer.StateName,
												)}
											</div>
										</div>
									</If>
									<If
										condition={
											(user.isLEA() || user.isCoordinator()) &&
											type === "submission"
										}
									>
										<div className="col-sm-6 text-center">
											<ProgressButton
												id="remind-officer"
												className={classNames("btn btn-primary btn-big", {
													"btn-success":
														progressButtons.remindOfficer ===
														"disabled",
												})}
												state={progressButtons.remindOfficer}
												onClick={this.remindOfficerSubmit}
											>
												{progressButtons.remindOfficer === "disabled"
													? "Notification Sent"
													: "Remind Officer"}
											</ProgressButton>
										</div>
									</If>
								</div>
							</div>
							<div id="invoice-breakdown">
								<div className="row">
									<div className="col-xs-9 col-md-10">
										<h3>{lang.t("Invoices Breakdown")}</h3>
									</div>
									<div className="col-xs-3 col-md-2 text-right">
										<div className="invoice-breakdown-actions">
											<div className="invoice-breakdown-actions">
												<button
													id="print-invoice"
													type="button"
													aria-label="print invoice"
													tabIndex={0}
													className="btn btn-link btn-lg rkf rkf-printer hidden-print"
													onClick={() => this.handleOnPrint()}
												/>
											</div>
											<div className="invoice-breakdown-actions">
												<button
													id="download-invoice"
													type="button"
													aria-label="download invoice"
													tabIndex={0}
													className="btn btn-link btn-lg rkf rkf-pdf hidden-print"
													onClick={() => this.downloadInvoicePDF()}
												/>
											</div>
										</div>
									</div>
								</div>
								<div id="breakdown-table">
									<div className="row">
										<div className="col-xs-3">
											<strong>{lang.t("Description")}</strong>
										</div>
										<div className="col-xs-2">
											<strong>{lang.t("Rate")}</strong>
										</div>
										<div className="col-xs-4">
											<strong>{lang.t("Quantity")}</strong>
										</div>
										<div className="col-xs-3 text-right">
											<strong>{lang.t("Total")}</strong>
										</div>
									</div>
									<div className="separator separator-solid" />
									<For
										each="item"
										of={invoice.Items || []}
									>
										<div
											key={`item-${item.Id}`}
											className="row"
										>
											<div className="col-xs-3">{item.ItemName}</div>
											<div className="col-xs-2">
												{utils.currencyFormatter(item.Price || 0)}
											</div>
											<div className="col-xs-4">
												{item.Quantity}
												<If condition={item.ClockIn && item.ClockOut}>
													<br />
													<small>
														{utils.formatTime(
															new Date(`${item.ClockIn}Z`),
															user.UserPreferences !==
																"useMilitaryTime",
															TimeZoneId,
														)}
														&nbsp;-&nbsp;
														{utils.formatTime(
															new Date(`${item.ClockOut}Z`),
															user.UserPreferences !==
																"useMilitaryTime",
															TimeZoneId,
														)}
													</small>
												</If>
											</div>
											<div className="col-xs-3 text-right">
												{utils.currencyFormatter(item.Total || 0)}
											</div>
										</div>
									</For>
									<If
										condition={
											invoice.TotalOfficerPay && invoice.Items.length > 0
										}
									>
										<div className="row form-group">
											<div className="col-xs-9 col-sm-4 col-sm-offset-5">
												<strong>{lang.t("Total Officer Pay")}</strong>
											</div>
											<div className="col-xs-3 text-right">
												<strong>
													{utils.currencyFormatter(
														invoice.TotalOfficerPay || 0,
													)}
												</strong>
											</div>
										</div>
									</If>
									<If
										condition={
											invoice.Coordinator && invoice.CoordinatorFees?.ItemName
										}
									>
										<div className="row">
											<div className="col-xs-3">
												{invoice.CoordinatorFees.ItemName}
											</div>
											<div className="col-xs-2">
												{utils.currencyFormatter(
													invoice.CoordinatorFees.Price || 0,
												)}
											</div>
											<div className="col-xs-4">
												{invoice.CoordinatorFees.Quantity}
											</div>
											<div className="col-xs-3 text-right">
												{utils.currencyFormatter(
													invoice.CoordinatorFees.Total || 0,
												)}
											</div>
										</div>
									</If>
									<If
										condition={
											invoice.TotalCoordinatorFee &&
											invoice.TotalCoordinatorFee > 0
										}
									>
										<div className="row form-group">
											<div className="col-xs-9 col-sm-4 col-sm-offset-5">
												<strong>{lang.t("Total Coordination Fees")}</strong>
											</div>
											<div className="col-xs-3 text-right">
												<strong>
													{utils.currencyFormatter(
														invoice.TotalCoordinatorFee,
													)}
												</strong>
											</div>
										</div>
									</If>
									<For
										each="fee"
										of={invoice.JurisdictionFees || []}
									>
										<div
											key={`jurisdictionFee-${fee.Id}`}
											className="row"
										>
											<div className="col-xs-3">{fee.ItemName}</div>
											<div className="col-xs-2">
												{utils.currencyFormatter(fee.Price || 0)}
											</div>
											<div className="col-xs-4">{fee.Quantity}</div>
											<div className="col-xs-3 text-right">
												{utils.currencyFormatter(fee.Total || 0)}
											</div>
										</div>
									</For>
									<If condition={invoice.JurisdictionVehicleFees}>
										<div className="row">
											<div className="col-xs-3">
												{invoice.JurisdictionVehicleFees.ItemName}
											</div>
											<div className="col-xs-2">
												{utils.currencyFormatter(
													invoice.JurisdictionVehicleFees.Price || 0,
												)}
											</div>
											<div className="col-xs-4">
												{invoice.JurisdictionVehicleFees.Quantity}
											</div>
											<div className="col-xs-3 text-right">
												{utils.currencyFormatter(
													invoice.JurisdictionVehicleFees.Total || 0,
												)}
											</div>
										</div>
									</If>
									<div className="row">
										<div className="col-xs-9 col-sm-4 col-sm-offset-5">
											<strong>{lang.t("Total Jurisdiction Fees")}</strong>
										</div>
										<div className="col-xs-3 text-right">
											<strong>
												{utils.currencyFormatter(
													invoice.TotalJurisdictionFees || 0,
												)}
											</strong>
										</div>
									</div>
									<div className="separator separator-solid" />
									<div className="row">
										<div className="col-xs-9 col-sm-4 col-sm-offset-5">
											<strong>{lang.t("Service Fees")}</strong>
										</div>
										<div className="col-xs-3 text-right">
											{utils.currencyFormatter(
												invoice.TotalServiceCharge || 0,
											)}
										</div>
										<If
											condition={
												invoice.WorkerCompensationFee &&
												invoice.WorkerCompensationFee > 0
											}
										>
											<div className="col-xs-9 col-sm-4 col-sm-offset-5">
												<strong>
													{lang.t("Workers' Compensation Fee")}
												</strong>
											</div>
											<div className="col-xs-3 text-right">
												{utils.currencyFormatter(
													invoice.WorkerCompensationFee,
												)}
											</div>
										</If>
										<div className="col-xs-9 col-sm-4 col-sm-offset-5">
											<strong>{lang.t("Taxes")}</strong>
										</div>
										<div className="col-xs-3 text-right">
											{utils.currencyFormatter(invoice.Tax || 0)}
										</div>
									</div>
									<div className="separator separator-solid" />
									<div className="row">
										<div className="col-xs-9 col-sm-4 col-sm-offset-5 text-uppercase">
											<strong>{lang.t("Total")}</strong>
										</div>
										<div className="col-xs-3 text-right">
											<strong>
												{utils.currencyFormatter(invoice.TotalInvoice || 0)}
											</strong>
										</div>
									</div>
								</div>
							</div>
							<div className="row notes-section">
								<Choose>
									<When condition={user.Organization.Id !== invoice.AgencyId}>
										<em className="col-xs-12">
											{invoice?.AgencyNotes?.split("\n").map(note => (
												<span key={note.replace(" ", "")}>
													{note}
													<br />
												</span>
											))}
										</em>
									</When>
									<Otherwise>
										<div className="col-xs-12 hidden-print">
											<label htmlFor="notes-input">{`${lang.t("Invoice")} ${lang.t("Disclaimer")}`}</label>
											&nbsp;
											<Tooltip
												content={lang.t(
													"This information will be shown to clients so they understand payment conditions",
												)}
											>
												<i className="hidden-xs rkf rkf-question-solid tooltip-icon" />
											</Tooltip>
										</div>
										<div className="col-xs-12">
											<textarea
												id="notes-input"
												name="notes-input"
												className="form-control hidden-print"
												rows="3"
												value={invoice.AgencyNotes}
												placeholder={lang.t("e_g_ Net 15 for all invoices")}
												onChange={e => this.agencyNotesChange(e)}
											/>
											{/* Keep formating such as line breaks for the AgencyNotes */}
											<em className="visible-print">
												{invoice?.AgencyNotes?.split("\n").map(note => (
													<span key={note.replace(" ", "")}>
														{note}
														<br />
													</span>
												))}
											</em>
										</div>
										<If condition={invoice.AgencyNotes !== originalAgencyNotes}>
											<div className="col-xs-12 col-sm-offset-9 col-sm-3 text-right hidden-print">
												<br />
												<button
													type="button"
													className="btn btn-primary btn-block"
													onClick={e => this.updateAgencyNotes(e)}
												>
													{lang.t("Save")}
												</button>
											</div>
										</If>
									</Otherwise>
								</Choose>
							</div>
						</div>
						<div className="col-sm-4 hidden-print invoice-summary">
							<div className="text-center invoice-actions">
								<h4 className="text-uppercase">{lang.t("Total Invoice")}</h4>
								<h1>{utils.currencyFormatter(invoice.TotalInvoice || 0)} USD</h1>
								<h5 className="text-muted">
									({lang.t("Estimated Amount")}:{" "}
									{utils.currencyFormatter(invoice.EstimatedInvoiceTotal || 0)})
								</h5>
								<If
									condition={
										invoice.StatusId !== INVOICE_STATUSES.PENDING_PROCESSING
									}
								>
									<div className="action-buttons text-center hidden-print">
										<Choose>
											<When
												condition={
													type === "submission" &&
													((user.isLEA() && user.IsStaffAdmin) ||
														user.isCoordinator()) &&
													user.Organization
														.IsOfficerInvoiceApprovalRequired
												}
											>
												<button
													type="button"
													className="btn btn-default btn-block"
													aria-label={lang.t("Change to Pending Review")}
													onClick={() =>
														this.handleInvoiceAction("toReview")
													}
												>
													{lang.t("Change to Pending Review")}
												</button>
											</When>
											<When
												condition={
													type === "review" &&
													(user.isSuperAdmin() ||
														user.isAgencyAdmin() ||
														user.isLEA() ||
														user.isCoordinator())
												}
											>
												<If
													condition={
														user.isSuperAdmin() || user.isAgencyAdmin()
													}
												>
													<button
														type="button"
														className="btn btn-primary btn-block"
														aria-label={lang.t("Approve Invoice")}
														onClick={() =>
															this.handleInvoiceAction("toPayment")
														}
													>
														{lang.t("Approve Invoice")}
													</button>
													<button
														type="button"
														className="btn btn-default btn-block"
														aria-label={lang.t("Dispute Invoice")}
														onClick={() =>
															this.handleInvoiceAction("dispute")
														}
													>
														{lang.t("Dispute Invoice")}
													</button>
												</If>
												<If
													condition={
														user.isSuperAdmin() ||
														user.IsStaffAdmin ||
														(user.isLEA() && user.IsInternalCoordinator)
													}
												>
													<button
														type="button"
														className="btn btn-default btn-block"
														aria-label={lang.t("Edit Invoice")}
														onClick={() =>
															this.handleInvoiceAction("edit")
														}
													>
														{lang.t("Edit Invoice")}
													</button>
													<If
														condition={
															(user.isLEA() ||
																user.isCoordinator()) &&
															user.Organization
																.IsOfficerInvoiceApprovalRequired
														}
													>
														<button
															type="button"
															className="btn btn-primary btn-block"
															aria-label={lang.t("Send Invoice")}
															onClick={() =>
																this.handleInvoiceAction("send")
															}
														>
															{lang.t("Send Invoice")}
														</button>
													</If>
												</If>
											</When>
											<When
												condition={
													type === "paid" &&
													!invoice.InvoiceEstimatePrePayment
												}
											>
												<button
													type="button"
													className="btn btn-primary btn-block"
													aria-label={lang.t("Show Receipt")}
													onClick={() => this.handleInvoiceAction("paid")}
												>
													{lang.t("Show Receipt")}
												</button>
											</When>
											<When
												condition={
													type === "payment" &&
													!user.isLEA() &&
													!user.isCoordinator()
												}
											>
												<button
													type="button"
													className="btn btn-primary btn-block"
													aria-label={lang.t("Pay Invoice")}
													onClick={() =>
														this.handleInvoiceAction("payInvoice")
													}
												>
													{lang.t("Pay Invoice")}
												</button>
												<If condition={user.isSuperAdmin()}>
													<button
														type="button"
														className="btn btn-default btn-block"
														aria-label={lang.t("Edit Invoice")}
														onClick={() =>
															this.handleInvoiceAction("edit")
														}
													>
														{lang.t("Edit Invoice")}
													</button>
												</If>
												<button
													type="button"
													className="btn btn-default btn-block"
													aria-label={lang.t("Dispute Invoice")}
													onClick={() =>
														this.handleInvoiceAction("dispute")
													}
												>
													{lang.t("Dispute Invoice")}
												</button>
												<button
													type="button"
													className="btn btn-default btn-block"
													aria-label={lang.t("Change to Pending Review")}
													onClick={() =>
														this.handleInvoiceAction("toReview")
													}
												>
													{lang.t("Change to Pending Review")}
												</button>
											</When>
											<When
												condition={
													type === "dispute" &&
													!user.isLEA() &&
													!user.isCoordinator()
												}
											>
												<button
													type="button"
													className="btn btn-default btn-block"
													aria-label={lang.t("Change to Pending Review")}
													onClick={() =>
														this.handleInvoiceAction("toReview")
													}
												>
													{lang.t("Change to Pending Review")}
												</button>
											</When>
										</Choose>
										<If condition={user.isSuperAdmin()}>
											<button
												type="button"
												className="btn btn-default btn-block"
												aria-label={lang.t("Re-Calculate Totals")}
												onClick={() =>
													this.handleInvoiceAction("recalculate")
												}
											>
												{lang.t("Re-Calculate Totals")}
											</button>
										</If>
										<If
											condition={
												type !== "dispute" &&
												user.isSuperAdmin() &&
												invoice.StatusId !== 11 &&
												invoice.Status !== "Void"
											}
										>
											<button
												type="button"
												className="btn btn-default btn-block"
												aria-label={lang.t("Void")}
												onClick={() => this.confirmVoid()}
											>
												{lang.t("Void")}
											</button>
										</If>
										<If
											condition={
												this.hasRemoveInsurance &&
												user.isSuperAdmin() &&
												type === "paid" &&
												invoice.RKInsurance > 0
											}
										>
											<div className="row">
												<div
													id="refund-menu"
													className="col-xs-4 col-sm-2"
													style={{ width: "100%" }}
												>
													<button
														type="button"
														className="btn btn-primary btn-block dropdown-toggle"
														style={{ width: "100%" }}
														onClick={() => this.toggleActionDropdown()}
														aria-haspopup="true"
														aria-expanded="false"
													>
														{lang.t("Issue Refund")}
														&nbsp;
														<span className="caret" />
													</button>
													<ul className="dropdown-menu dropdown-menu-right">
														<If condition={invoice.RKInsurance > 0}>
															<li>
																<button
																	type="button"
																	id="1"
																	className="btn btn-block text-left"
																	onClick={() =>
																		this.refundInsurance()
																	}
																>
																	{lang.t("RKInsurance refund")}
																</button>
															</li>
														</If>
													</ul>
												</div>
											</div>
										</If>
									</div>
								</If>
								<If
									condition={
										invoice.StatusId ===
											INVOICE_STATUSES.PENDING_CLIENT_REVIEW &&
										(((user.isSuperAdmin() ||
											user.isLEA() ||
											user.IsInternalCoordinator) &&
											user.IsStaffAdmin) ||
											user.isCoordinator())
									}
								>
									<ProgressButton
										className={classNames("btn btn-primary btn-block", {
											"btn-success":
												progressButtons.resendInvoice === "disabled",
										})}
										state={progressButtons.resendInvoice}
										onClick={this.resendInvoice}
									>
										{progressButtons.resendInvoice === "disabled"
											? "Invoice Sent"
											: "Re-send Invoice"}
									</ProgressButton>
								</If>
							</div>
							<div className="row">
								<div className="col-xs-6">
									<strong>{lang.t("Scheduled Time")}</strong>
									<br />
									<small>
										{utils.formatTime(
											new Date(`${invoice.JobStartDateTime}Z`),
											user.UserPreferences !== "useMilitaryTime",
											TimeZoneId,
										)}
										-
										{utils.formatTime(
											new Date(`${invoice.JobEndDateTime}Z`),
											user.UserPreferences !== "useMilitaryTime",
											TimeZoneId,
										)}
									</small>
								</div>
								<div className="col-xs-6">
									<strong>{`${lang.t("Clock In")} / ${lang.t("Clock Out")}`}</strong>
									<br />
									<small>
										{utils.formatTime(
											new Date(`${invoice.ClockInDateTime}Z`),
											user.UserPreferences !== "useMilitaryTime",
											TimeZoneId,
										)}
										-
										{utils.formatTime(
											new Date(`${invoice.ClockOutDateTime}Z`),
											user.UserPreferences !== "useMilitaryTime",
											TimeZoneId,
										)}
									</small>
								</div>
							</div>
							<br />
							<div
								className="row"
								role="button"
								tabIndex="0"
								aria-label="open notes panel"
								onClick={() => {
									this.toggleCollapsingPanel("notes");
								}}
								onKeyPress={({ key }) => {
									if (key === " ") this.toggleCollapsingPanel("notes");
								}}
							>
								<div className="col-xs-10">
									<h4>{`${lang.t("Officer Notes")} (${invoice?.Notes?.length})`}</h4>
								</div>
								<div className="col-xs-2">
									<i
										className={`h4 rkf ${panelTabsExpanded.notes ? "rkf-chevron-up" : "rkf-chevron-down"}`}
									/>
								</div>
								<div
									className={classNames("col-xs-12", "collapsing-panel", {
										expanded: panelTabsExpanded.notes,
									})}
								>
									<Choose>
										<When condition={invoice?.Notes?.length > 0}>
											<For
												each="note"
												of={invoice.Notes || []}
											>
												<br />
												<div
													key={`note-${note.Id}`}
													className="row"
												>
													<div className="col-xs-6">
														<small>
															<em>{`${note.UserName} ${lang.t("added a Note")}`}</em>
														</small>
													</div>
													<div className="col-xs-6 text-right">
														<small>
															<em>
																{utils.formatDate(
																	new Date(
																		`${note.CreateDateUTC}Z`,
																	),
																	"en-US",
																	TimeZoneId,
																	user.UserPreferences ===
																		"useMilitaryTime"
																		? "dt-short-mil"
																		: "dt-short",
																)}
															</em>
														</small>
													</div>
													<div className="col-xs-12">
														<small>{note.Note}</small>
													</div>
												</div>
											</For>
										</When>
										<Otherwise>
											<small>{lang.t("No notes")}</small>
										</Otherwise>
									</Choose>
								</div>
							</div>
							<div className="separator separator-solid" />
							<div
								className="row"
								role="button"
								tabIndex="0"
								aria-label="open attachments panel"
								onClick={() => {
									this.toggleCollapsingPanel("attachments");
								}}
								onKeyPress={({ key }) => {
									if (key === " ") this.toggleCollapsingPanel("attachments");
								}}
							>
								<div className="col-xs-10">
									<h4>{`${lang.t("Attachments")} (${invoice.Attachments?.length})`}</h4>
								</div>
								<div className="col-xs-2">
									<i
										className={`h4 rkf ${panelTabsExpanded.attachments ? "rkf-chevron-up" : "rkf-chevron-down"}`}
									/>
								</div>
								<div
									className={classNames("col-xs-12", "collapsing-panel", {
										expanded: panelTabsExpanded.attachments,
									})}
								>
									<Choose>
										<When condition={invoice.Attachments?.length > 0}>
											<div className="row">
												<div className="col-xs-8">
													<i className="rkf rkf-paperclip" />
													<small> {lang.t("Uploaded Files")}</small>
												</div>
												<div className="col-xs-2">
													<button
														type="button"
														className="btn btn-link btn-sm btn-block"
														onClick={this.downloadAllAttachments}
													>
														{lang.t("Download All")}
													</button>
												</div>
											</div>
											<For
												each="attachment"
												of={invoice.Attachments || []}
											>
												<div
													key={`attachment-${attachment.Id}`}
													className="row row-no-gutters"
												>
													<div className="col-xs-5">
														<small>
															<em>
																{utils.formatDate(
																	new Date(
																		`${attachment.CreateDateUTC}Z`,
																	),
																	"en-US",
																	TimeZoneId,
																	user.UserPreferences ===
																		"useMilitaryTime"
																		? "dt-short-mil"
																		: "dt-short",
																)}
															</em>
														</small>
													</div>
													<div className="col-xs-4">
														<a
															target="_blank"
															rel="noopener noreferrer"
															href={attachment.AttachmentUrl}
															download={attachment.FileName}
														>
															<small>{attachment.FileName}</small>
														</a>
													</div>
													<div className="col-xs-3">
														<small>
															<em>{`${lang.t("By")} ${attachment.UserName}`}</em>
														</small>
													</div>
												</div>
											</For>
										</When>
										<Otherwise>
											<small>{lang.t("No Attachments")}</small>
										</Otherwise>
									</Choose>
								</div>
							</div>
							<div className="separator separator-solid" />
							<div
								className="row"
								role="button"
								tabIndex="0"
								aria-label="open status panel"
								onClick={() => {
									this.toggleCollapsingPanel("statusHistory");
								}}
								onKeyPress={({ key }) => {
									if (key === " ") this.toggleCollapsingPanel("statusHistory");
								}}
							>
								<div className="col-xs-10">
									<h4>{`${lang.t("Status history")} (${invoice.StatusHistory?.length})`}</h4>
								</div>
								<div className="col-xs-2">
									<i
										className={`h4 rkf ${panelTabsExpanded.statusHistory ? "rkf-chevron-up" : "rkf-chevron-down"}`}
									/>
								</div>
								<div
									className={classNames("col-xs-12", "collapsing-panel", {
										expanded: panelTabsExpanded.statusHistory,
									})}
								>
									<Choose>
										<When condition={invoice?.StatusHistory?.length > 0}>
											<ul className="timeline">
												<For
													each="item"
													of={invoice.StatusHistory}
												>
													<li key={`note_${item.Id}`}>
														{utils.createStatusHistoryText(
															item.StatusId,
															item.Status,
															item.UpdatedBy,
														)}
														<br />
														<small>
															{utils.formatDate(
																new Date(`${item.DateTime}Z`),
																"en-US",
																TimeZoneId,
																user.UserPreferences ===
																	"useMilitaryTime"
																	? "dt-short-mil"
																	: "dt-short",
															)}
														</small>
													</li>
												</For>
											</ul>
										</When>
										<Otherwise>
											<small>{lang.t("No status history")}</small>
										</Otherwise>
									</Choose>
								</div>
							</div>
						</div>
					</div>
				</div>
				<ConfirmationModal
					ref={this._confirmationModal}
					size="sm"
				/>
				<DisputeInvoiceModal
					ref={this._disputeInvoiceModal}
					onSubmit={() => push({ pathname: "/payments/list/review" })}
				/>
				<EditInvoiceModal
					invoiceId={invoice.Id}
					ref={this._editInvoiceModal}
					onSave={this.getInvoice}
					user={user}
				/>
				<SendToContactModal
					show={showSendInvoiceModal}
					onHide={this.toggleSendInvoiceModal}
					clientId={invoice.ClientId}
					onSubmit={(contactId, additionalContacts) =>
						this.sendInvoice(invoice.Id, contactId, additionalContacts)
					}
					isOwnAgency={invoice.AgencyId === Organization.Id}
				/>
				<Loader active={loading} />
			</div>
		);
	}
}

PaymentDetails.propTypes = {
	updateStoreProperties: PropTypes.func.isRequired,
	router: PropTypes.object.isRequired,
	user: PropTypes.object,
	flags: PropTypes.object.isRequired,
};

PaymentDetails.defaultProps = {
	user: {},
};

const mapDispatchToProps = dispatch => ({
	updateStoreProperties: bindActionCreators(Actions, dispatch).updateStoreProperties,
});

export default withLDConsumer()(connect(undefined, mapDispatchToProps)(PaymentDetails));
